import type { Optional } from '@festina-life/core'
import { Icon, type IconName } from '@festina-life/ui/structure'
import { Fragment, type ReactNode, useMemo } from 'react'

import { compareValues, type ValueComparison } from '../fplusDiff/diffModel'
import { useHideInheritanceDiff } from './useHideInheritanceDiff'
import { useCompareContext } from './valueSetupCompareContext'

const DiffIconByType: Record<
  Exclude<ValueComparison, 'unchanged'>,
  IconName
> = {
  added: 'PlusCircle',
  removed: 'MinusCircle',
  modified: 'ChevronDoubleRight',
}

type DiffingEntry<T> = {
  current: Optional<T>
  compare: Optional<T>
  result: ValueComparison
}

type Props<T> = {
  title: string
  current: Optional<T[]>
  compare: Optional<T[]>
  renderValue: (value: T, origin: 'compare' | 'current') => ReactNode
  keyFn: (value: T) => Optional<string>
}

export const ListValuesDiff = <T,>({
  title,
  compare,
  current,
  renderValue,
  keyFn,
}: Props<T>) => {
  const { compareType } = useCompareContext()
  const currentValuesMap = useMemo(
    () =>
      new Map(
        (current ?? []).map((currentEntry) => [
          keyFn(currentEntry) ?? crypto.randomUUID(),
          currentEntry,
        ]),
      ),
    [current, keyFn],
  )
  const compareValuesMap = useMemo(
    () =>
      new Map(
        (compare ?? []).map((compareEntry) => [
          keyFn(compareEntry) ?? crypto.randomUUID(),
          compareEntry,
        ]),
      ),
    [compare, keyFn],
  )

  const diffingData: Map<string, DiffingEntry<T>> = useMemo(() => {
    const allKeys = Array.from(
      new Set([
        ...Array.from(currentValuesMap.keys()),
        ...Array.from(compareValuesMap.keys()),
      ]),
    )

    return new Map(
      allKeys.map((entryKey) => {
        const currentEntry = currentValuesMap.get(entryKey)
        const compareEntry = compareValuesMap.get(entryKey)
        const comparison = compareValues(compareEntry, currentEntry)

        /**
         * An entry that only one side has says nothing about a value against
         * the parent it was never given: it is the absence itself, which the
         * inheritance rule keeps out of the way rather than reporting as a
         * change.
         */
        const isInheritanceDiffAgainstEmptyValue =
          compareType === 'inherited' &&
          (comparison === 'added' || comparison === 'removed')

        return [
          entryKey,
          {
            current: currentEntry,
            compare: compareEntry,
            result: isInheritanceDiffAgainstEmptyValue
              ? 'unchanged'
              : comparison,
          },
        ]
      }),
    )
  }, [compareType, compareValuesMap, currentValuesMap])

  const isHidingInheritanceDiff = useHideInheritanceDiff(current)
  const allChanges = Array.from(diffingData.entries()).filter(
    ([, entry]) => entry.result !== 'unchanged',
  )
  const areChangesMade = !!allChanges.length

  if (!areChangesMade || isHidingInheritanceDiff) {
    return null
  }

  return (
    <div>
      <div className="mb-2 text-center font-medium">{title}</div>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-8">
        {allChanges.map(([entryKey, diffEntry]) => {
          const diffIconName =
            DiffIconByType[
              diffEntry.result as Exclude<ValueComparison, 'unchanged'>
            ]

          return (
            <Fragment key={`edit-${entryKey}`}>
              {diffEntry.compare ? (
                renderValue(diffEntry.compare, 'compare')
              ) : (
                <div />
              )}
              <Icon name={diffIconName} />
              {diffEntry.current ? (
                renderValue(diffEntry.current, 'current')
              ) : (
                <div />
              )}
            </Fragment>
          )
        })}
      </div>
    </div>
  )
}
